<template>
  <section>
    <alert-banner v-if="shouldShowAlertBanner" :status="alertBannerStatus" class="w-full" :autoScrollToBanner="false">
      <span v-if="isPending">Job post under review</span>
      <span v-if="totalNewApplicants > 0">{{ newApplicantsTitle }}</span>
    </alert-banner>
    <div class="dashboard-job-module__row-item gap-4 relative">
      <a class="text-primary w-full" :href="jobLink">
        <h3 :class="titleClasses">{{ job.title }}</h3>
        <p :class="applicantsClasses">{{ totalApplicantsText }}</p>
        <p v-if="!fcOwnsJob" class="dashboard-job-module__content xs-vertical-spacing body-2-meta">{{ jobDate }}</p>
        <p v-else-if="fcOwnsJob" class="dashboard-job-module__content xs-vertical-spacing body-2-meta">
          Posted by Family Concierge
        </p>
      </a>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import pluralize from 'pluralize';
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import JobPresenter from '@/lib/presenters/job_presenter';
import helperStatus from '@/helpers/status';

interface Job {
  id: string;
  title: string;
  status: string;
  isActive: boolean;
  parent: {
    id: string;
  };
  applications: {
    edges: Array<{
      node: {
        status: string;
        parentInterestStatus: string;
      };
    }>;
    totalCount: number;
  };
}

export default defineComponent({
  name: 'JobDescription',
  components: {
    AlertBanner,
  },
  props: {
    job: {
      type: Object as () => Job,
      required: true,
    },
    accountID: {
      type: String,
      required: true,
    },
    pendingStatus: {
      type: Number,
      default: () => helperStatus.Warn,
    },
  },
  data() {
    return {
      helperStatus,
    };
  },
  computed: {
    isPending(): boolean {
      return this.job.status === 'PENDING';
    },
    isActive(): boolean {
      return this.job.isActive;
    },
    isActiveOrPending(): boolean {
      return this.isActive || this.isPending;
    },
    totalNewApplicants(): number {
      const receivedApplications = this.job.applications.edges.filter(({ node }) => node.parentInterestStatus === 'RECEIVED');
      return receivedApplications.length;
    },
    newApplicantsTitle(): string {
      return `${this.totalNewApplicants} received ${pluralize('applicant', this.totalNewApplicants)}`;
    },
    totalApplicantsText(): string {
      const { totalCount } = this.job.applications;
      return `${totalCount} total ${pluralize('applicant', totalCount)}`;
    },
    jobDate(): string {
      return new JobPresenter(this.job).dateDescription();
    },
    titleClasses(): string {
      let titleClasses = 'dashboard-job-module__content xs-vertical-spacing-top ';
      titleClasses += this.isActiveOrPending ? 'heading-5' : 'heading-5-meta';
      return titleClasses;
    },
    applicantsClasses(): string {
      let applicantsClasses = 'dashboard-job-module__content ';
      applicantsClasses += this.isActiveOrPending ? 'body-2' : 'body-2-meta';
      return applicantsClasses;
    },
    fcOwnsJob(): boolean {
      return !(this.accountID === this.job.parent.id);
    },
    jobLink(): string {
      if (this.totalNewApplicants > 0) {
        return `/jobs/${this.job.id}/applications`;
      }
      if (!this.isActiveOrPending || this.job.status === 'JOB_STATUS_CLOSED') {
        return '/seeker/my-jobs/closed';
      }
      return '/seeker/my-jobs/opened';
    },
    shouldShowAlertBanner(): boolean {
      return this.isPending || this.totalNewApplicants > 0;
    },
    alertBannerStatus(): number {
      if (this.totalNewApplicants > 0) return helperStatus.Error;
      return helperStatus.Warn;
    },
  },
});
</script>
